import type {
  ControlPlaneCapability,
  UsageRecoveryAuthorizationResult,
} from '@internal/spassu-code-contract';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { type RefObject, useEffect, useRef, useState } from 'react';
import type { ControlPlaneManagementView } from '../hooks/useControlPlaneManagement';
import { CapabilityButton } from './ControlPlaneCapabilityButton';

const useStyles = makeStyles(theme => ({
  section: { display: 'grid', gap: theme.spacing(1) },
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  command: {
    overflowWrap: 'anywhere',
    fontFamily: 'monospace',
  },
}));

export function ControlPlaneUsageRecoveryPanel(props: {
  management: ControlPlaneManagementView;
  capability: ControlPlaneCapability;
  activityTitle: string;
  busy: boolean;
}) {
  const classes = useStyles();
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [copyAnnouncement, setCopyAnnouncement] = useState('');
  const resultRef = useRef<HTMLHeadingElement | null>(null);
  const authorization = props.management.usageRecoveryAuthorization;
  useEffect(() => {
    if (authorization) resultRef.current?.focus();
  }, [authorization]);

  const copy = async (label: string, value: string) =>
    setCopyAnnouncement(await clipboardAnnouncement(label, value));

  // The panel follows the capability (RN-CONEX-01): a reader who cannot recover
  // usage sees no heading, no explanation and no button. The one-use material
  // just issued stays on screen even though the next poll denies a second
  // recovery (USAGE_RECOVERY_ACTIVE) — hiding it would burn the code.
  if (!props.capability.allowed && !authorization) return null;
  return (
    <Box className={classes.section}>
      <UsageRecoveryIntroduction />
      <CapabilityButton
        capability={props.capability}
        label="Recuperar tokens"
        busy={props.busy}
        onClick={() => setConfirmationOpen(true)}
      />
      <RecoveryConfirmationDialog
        open={confirmationOpen}
        busy={props.busy}
        activityTitle={props.activityTitle}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={() => {
          setConfirmationOpen(false);
          void props.management.authorizeUsageRecovery();
        }}
      />
      {authorization ? (
        <RecoveryAuthorization
          authorization={authorization}
          announcement={copyAnnouncement}
          resultRef={resultRef}
          onCopy={copy}
        />
      ) : null}
    </Box>
  );
}

async function clipboardAnnouncement(
  label: string,
  value: string,
): Promise<string> {
  try {
    await navigator.clipboard.writeText(value);
    return `${label} copiado.`;
  } catch {
    return `${label} não pôde ser copiado. Selecione o texto manualmente.`;
  }
}

function RecoveryConfirmationDialog(props: {
  open: boolean;
  busy: boolean;
  activityTitle: string;
  onClose(): void;
  onConfirm(): void;
}) {
  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      aria-labelledby="usage-recovery-confirmation-title"
    >
      <DialogTitle id="usage-recovery-confirmation-title">
        Autorizar recuperação desta atividade?
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Atividade: {props.activityTitle}. Serão extraídos somente identidade,
          timestamp, modelo e contagens de tokens. O transcript e seu caminho
          não serão enviados.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={props.busy}>
          Cancelar
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={props.busy}
          onClick={props.onConfirm}
        >
          Autorizar recuperação
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RecoveryAuthorization(props: {
  authorization: UsageRecoveryAuthorizationResult;
  announcement: string;
  resultRef: RefObject<HTMLHeadingElement>;
  onCopy(label: string, value: string): Promise<void>;
}) {
  const classes = useStyles();
  const { authorization } = props;
  return (
    <Box className={classes.section} aria-label="Recuperação autorizada">
      <Typography ref={props.resultRef} tabIndex={-1} variant="h4">
        Recuperação autorizada
      </Typography>
      <Typography variant="body2">
        Válida até {new Date(authorization.recovery.expiresAt).toLocaleString()}
        . Execute no clone correto e informe o código separado quando
        solicitado.
      </Typography>
      <Typography className={classes.command} variant="body2">
        Comando: {authorization.command}
      </Typography>
      <Typography className={classes.command} variant="body2">
        Código de uso único: {authorization.recoveryCode}
      </Typography>
      <RecoveryCopyActions
        authorization={authorization}
        onCopy={props.onCopy}
      />
      <Typography aria-live="polite" aria-atomic="true">
        {props.announcement}
      </Typography>
    </Box>
  );
}

function RecoveryCopyActions(props: {
  authorization: UsageRecoveryAuthorizationResult;
  onCopy(label: string, value: string): Promise<void>;
}) {
  const classes = useStyles();
  return (
    <Box className={classes.row}>
      <Button
        variant="outlined"
        onClick={() =>
          void props.onCopy('Comando', props.authorization.command)
        }
      >
        Copiar comando
      </Button>
      <Button
        variant="outlined"
        onClick={() =>
          void props.onCopy('Código', props.authorization.recoveryCode)
        }
      >
        Copiar código
      </Button>
    </Box>
  );
}

function UsageRecoveryIntroduction() {
  return (
    <>
      <Typography variant="h6" component="h3">
        Recuperação de tokens Claude
      </Typography>
      <Typography variant="body2">
        A leitura ocorre somente no clone que possui a sessão vinculada. O
        Control Plane recebe identidade, timestamp, modelo e contagens.
      </Typography>
    </>
  );
}
